import React, { Fragment, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { DropdownList } from "react-widgets";
import { toastr } from "react-redux-toastr";
import moment from "moment";
import api from "../../../../api";
import { AutoCompleteInput } from "../../../../components/form-group";
import ErrorMessage from "../../../../components/text/ErrorMessage";

import {
  IAddLeadActivityModel,
  VehicleModelMinimal,
  VehicleVariantMinimal,
  Lead,
  Customer,
  capitalizeFirstLetter,
} from "../../../../core";
import { useLeadActivities } from "../../../../core/hooks/useLeadActivities";
import { combineDateAndTime } from "../../../../utils/CombineDateAndTime";
import LeadActivityAutoCompleteInput from "../../../../components/form-group/lead-activity-autocomplete-input";
import { ITimePeriodData } from "../../../../core/components/time-period/custom-time-period";
import {
  isTimePeriodValid,
  SingleDayTimePeriod,
} from "../../../../core/components/time-period/single-day-time-period";
import classnames from "classnames";
import { useAppSettings } from "../../../../core/hooks/useAppSettings";
import { checkIfExcludedDatesBeBlocked } from "../../../../core/utils/event-utils";
import SalesPersonsAutoCompleteInput from "../../../../components/form-group/sales-persons-auto-complete-input";

interface IAddActivityProps {
  leadId?: string;
  documentId?: string;
  activity?: ActivityModel;
  onSubmit?: (activity: ActivityModel) => void;
  onCancel?: () => void;
  isEdit?: boolean;
  onSubmitCompleted?: () => void;
  onDateChange?: (date: Date) => void;
  alwaysShowform?: boolean;
  onSuccess?: () => void;
  onChangeAssignee?: (id: string) => void;
  onTimePeriodChange?: (data?: ITimePeriodData) => void;
  excludedDates?: Date[];
  onActivityTypeChange?: (type: string) => void;
  disableTransactionExecutive?: boolean;
}

export const AddActivityWraper: React.FunctionComponent<IAddActivityProps> = (
  props
) => {
  const { addLeadActivity } = useLeadActivities(api.lead);
  const [showEditor, setShowEditor] = useState(false);
  const alwaysShowform = props.alwaysShowform ?? false;

  const onSubmit = async (data: ActivityModel) => {
    const startDate = combineDateAndTime(
      data.timePeriod?.startDate!,
      data.timePeriod?.startTime!
    );
    const endDate = combineDateAndTime(
      data.timePeriod?.startDate!,
      data.timePeriod?.endTime!
    );

    const apiModel: IAddLeadActivityModel = {
      type: data.type!,
      title: data.title!,
      startDate,
      endDate,
      salesPerson: typeof data.salesPerson?._id === 'string' ? [data.salesPerson?._id] : data.salesPerson?._id,
      leadId: data.leadId as any,
      note: data.note,
      documentId: props.documentId,
    };
    const forAll = false;

    addLeadActivity(
      apiModel,
      () => {
        toastr.success("Success", "Activity added");
        setShowEditor(false);
        if (props.onSuccess) {
          props.onSuccess();
        }
        props.onSubmitCompleted?.();
      },
      forAll
    );
  };

  return (
    <Fragment>
      {!showEditor && !alwaysShowform && (
        <div
          onClick={() => {
            setShowEditor(true);
          }}
          className="border-bottom p-4 text-gray-400 font-xs"
        >
          Click here to add activity...
        </div>
      )}
      {(showEditor || alwaysShowform) && (
        <div className={classnames({ "px-4 ": !alwaysShowform, "mt-4": true })}>
          <AddActivityForm
            leadId={props.leadId}
            documentId={props.documentId}
            onDateChange={props.onDateChange}
            activity={props.activity}
            onChangeAssignee={(id: string) => {
              if (props.onChangeAssignee) {
                props.onChangeAssignee(id);
              }
            }}
            onCancel={() => {
              setShowEditor(false);
              if (props.onCancel) {
                props.onCancel();
              }
            }}
            onSuccess={() => {
              setShowEditor(false);
              if (props.onSuccess) {
                props.onSuccess();
              }
            }}
            onSubmit={(activity: ActivityModel) => {
              onSubmit(activity);
            }}
            disableTransactionExecutive={props.disableTransactionExecutive}
          ></AddActivityForm>
        </div>
      )}
    </Fragment>
  );
};

export const AddActivity: React.FunctionComponent<IAddActivityProps> = (
  props
) => {
  const [showEditor, setShowEditor] = useState(false);

  return (
    <Fragment>
      {!showEditor && (
        <div
          onClick={() => {
            setShowEditor(true);
          }}
          className="border-bottom p-4 text-gray-400 font-xs"
        >
          Click here to add activity...
        </div>
      )}
      {showEditor && (
        <div>
          <AddActivityForm
            leadId={props.leadId}
            documentId={props.documentId}
            onDateChange={props.onDateChange}
            activity={props.activity}
            onCancel={() => {
              setShowEditor(false);
            }}
            onSubmitCompleted={() => {
              setShowEditor(false);
              if (props.onSuccess) {
                props.onSuccess();
              }
            }}
            onSuccess={() => {
              setShowEditor(false);
              if (props.onSuccess) {
                props.onSuccess();
              }
            }}
          ></AddActivityForm>
        </div>
      )}
    </Fragment>
  );
};

export const AddActivityForm: React.FunctionComponent<IAddActivityProps> = (
  props
) => {
  const appSettings = useAppSettings(api.settings);
  const { getActivityIcon } = useLeadActivities(api.lead);
  const activity = props.activity;
  const { control, handleSubmit, errors, setValue } = useForm<ActivityModel>({
    reValidateMode: "onChange",
    mode: "onSubmit",
  });
  const buttons: IconButtonModel[] = [
    { placeholder: "Call", type: "call" },
    { placeholder: "Meeting", type: "meeting" },
    // { placeholder: "Deadline", type: "deadline" },
    { placeholder: "Task", type: "task" },
  ];
  const [selectedType, setSelectedType] = useState<string>();
  useEffect(() => {
    if (props.activity?.type != null) {
      setSelectedType(props.activity?.type);
    } else {
      setSelectedType(buttons[0].type);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const getSelectedObject = (): IconButtonModel => {
    if (selectedType != null) {
      const obj = buttons.find((p) => p.type === selectedType);
      if (obj != null) {
        return obj;
      }
    }
    return buttons[0];
  };
  useEffect(() => {
    if (selectedType && props.onActivityTypeChange) {
      props.onActivityTypeChange(selectedType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedType]);
  const { addLeadActivity, updating } = useLeadActivities(api.lead);

  const [isSalesPersonChanging, setSalesPersonChanging] = useState(false);

  const onSubmit = async (data: ActivityModel) => {
    if (!props.isEdit) {
      const startDate = combineDateAndTime(
        data.timePeriod?.startDate!,
        data.timePeriod?.startTime!
      );
      const endDate = combineDateAndTime(
        data.timePeriod?.startDate!,
        data.timePeriod?.endTime!
      );

      const apiModel: IAddLeadActivityModel = {
        type: selectedType!,
        title: data.title!,
        startDate,
        endDate,
        salesPerson: typeof data.salesPerson?._id === 'string' ? [data.salesPerson?._id] : data.salesPerson?._id,
        leadId: data.leadId as any,
        note: data.note,
        busyEvent: data.busyEvent,
        documentId: props.documentId,
      };
      const forAll = false;
      addLeadActivity(
        apiModel,
        () => {
          toastr.success("Success", "Activity added");
          props.onSuccess?.();
          props.onSubmitCompleted?.();
        },
        forAll
      );
    } else {
      if (props.onSubmit) {
        data.type = selectedType as any;
        props.onSubmit(data);
      }
    }
  };

  const renderLeadOption = () => {
    return (
      <Controller
        control={control}
        rules={{ required: "Lead is Required" }}
        name="leadId"
        defaultValue={props.leadId}
        render={() => <></>}
      />
    );
  };

  let minDate: any;
  if (
    appSettings.data?.leadActivityAssignment
      ?.noOfDaysToAllowBackdatedActivity !== undefined &&
    appSettings.data?.leadActivityAssignment
      ?.noOfDaysToAllowBackdatedActivity >= 0
  ) {
    minDate = new Date().setDate(
      new Date().getDate() -
        appSettings.data?.leadActivityAssignment
          ?.noOfDaysToAllowBackdatedActivity
    );
  }
  
  useEffect(() => {
    if(selectedType && !activity?.title){
      if(selectedType === 'emailOut'){
        setValue(
          "title",
          'Email'
        );
      }else if(selectedType === 'coldCalling'){
        setValue(
          "title",
          'Cold Calling'
        );
      }else{
        setValue(
          "title",
          capitalizeFirstLetter(selectedType)
        );
      }
    }
  }, [selectedType])

  return (
    <Fragment>
      <div
        className="tab-pane fade  show active"
        id="activity-all"
        role="tabpanel"
        aria-labelledby="activity-tab"
      >
        <div className="mt-4">
          <div className="form-group font-weight-semibold mb-3">
            <Controller
              control={control}
              rules={{ required: true }}
              name="title"
              defaultValue={activity?.title || 'Call'}
              render={({ onChange, value, name }) => (
                <input
                  type="text"
                  className="form-control rounded-sm font-weight-semibold"
                  value={value}
                  onChange={onChange}
                  placeholder={getSelectedObject().placeholder}
                />
              )}
            />
            {errors.title && <ErrorMessage>Title is required</ErrorMessage>}
          </div>

          <div className="input-group col-10 px-0 input-group-box mb-3">
            {buttons.map((p, index) => {
              return (
                <button
                  onClick={() => {
                    setSelectedType(p.type);
                  }}
                  className="form-control p-0"
                >
                  <i
                    data-toggle="tooltip"
                    title={p.placeholder}
                    className={
                      getActivityIcon(p.type) +
                      " " +
                      (p.type === selectedType ? "text-primary" : "")
                    }
                  ></i>
                </button>
              );
            })}
          </div>
          <form id={"activity-form"} onSubmit={handleSubmit(onSubmit)}>
            <div className="form-group font-weight-semibold mb-3">
              <label htmlFor="" className="font-sm text-uppercase">
                DATE AND TIME<span className="text-danger">*</span>
              </label>
              <div key={JSON.stringify(activity?.timePeriod ?? {})}>
                <Controller
                  control={control}
                  rules={{ required: true }}
                  name="timePeriod"
                  defaultValue={activity?.timePeriod}
                  render={({ onChange, value, name }) => (
                    <SingleDayTimePeriod
                      onChange={(val: ITimePeriodData) => {
                        if (isTimePeriodValid(val)) {
                          onChange(val);
                          if (props.onTimePeriodChange) {
                            props.onTimePeriodChange(val);
                          }
                        } else {
                          onChange(undefined);
                          if (props.onTimePeriodChange) {
                            props.onTimePeriodChange(undefined);
                          }
                        }
                      }}
                      defaultValue={activity?.timePeriod}
                      minDate={minDate}
                      appointmentTime={
                        appSettings?.data?.application?.appointmentTime
                      }
                      excludeDates={
                        checkIfExcludedDatesBeBlocked(selectedType)
                          ? props.excludedDates?.map((date) => new Date(date))
                          : []
                      }
                    ></SingleDayTimePeriod>
                  )}
                />
              </div>
              {errors.timePeriod && (
                <ErrorMessage>Please enter a valid time period</ErrorMessage>
              )}
            </div>

            <div className="form-group font-weight-semibold mb-3">
              <label htmlFor="" className="font-sm text-uppercase">
                COMMENTS
              </label>
              <Controller
                control={control}
                name="note"
                defaultValue={activity?.note}
                render={({ onChange, value, name }) => (
                  <textarea
                    rows={4}
                    className="form-control rounded-sm font-weight-semibold"
                    value={value}
                    onChange={onChange}
                    placeholder={"Comments"}
                  />
                )}
              />
              {errors.note && <ErrorMessage>Comment is required</ErrorMessage>}
            </div>

            <div
              key={activity?.salesPerson?._id ?? "" + "other-action-salesman"}
              className="form-group font-weight-semibold mb-3"
            >
              <label htmlFor="" className="font-sm text-uppercase">
                Transaction Manager
              </label>
              <Controller
                control={control}
                // rules={{ required: true }}
                name="salesPerson"
                defaultValue={activity?.salesPerson}
                render={({ onChange, value, name }) => (
                  <SalesPersonsAutoCompleteInput
                    loadingText={isSalesPersonChanging ? "Assigning ..." : ""}
                    defaultValue={value}
                    placeholder={"User"}
                    onChange={(val) => {
                      onChange(val);
                      if (props.onChangeAssignee) {
                        props.onChangeAssignee(val._id!);
                      }
                    }}
                    // apiUrl="api/user/sales-persons"
                    valueField={"name"}
                    dataLoader={(query?: string) => {
                      return api.customer.searchStaff(
                        {
                          query,
                          hideBlocked: true,
                        },
                        "lead-assignees"
                      );
                    }}
                    disabled={(activity?.salesPerson && props.disableTransactionExecutive) || updating}
                    hideBlocked={true}
                  />
                  // <AutoCompleteInput
                  //   defaultValue={value}
                  //   placeholder={'Transaction Manager'}
                  //   onChange={(currentValue) => {
                  //     onChange(currentValue);
                  //     if (props.onChangeAssignee) {
                  //       props.onChangeAssignee(currentValue._id!);
                  //     }
                  //   }}
                  //   apiUrl="api/user/sales-persons"
                  //   dataLoader={(query?: string) => {
                  //     return api.customer.searchSalesPerson(
                  //       {
                  //         query,
                  //         hideBlocked: true,
                  //       },
                  //       'other-activity'
                  //     );
                  //   }}
                  //   valueField={'name'}
                  // />
                )}
              />
              {errors.salesPerson && (
                <ErrorMessage>Transaction Manager is required</ErrorMessage>
              )}
            </div>
            {/* <div className="form-group font-weight-semibold mb-3">
              <label htmlFor="" className="font-sm text-uppercase">
                TYPE
              </label>
              <Controller
                control={control}
                name="busyEvent"
                defaultValue={activity?.busyEvent || true}
                render={({ onChange, value, name }) => (
                  <DropdownList
                    value={value}
                    data={[
                      { label: "Busy", value: true },
                      // { label: 'Free', value: false },
                    ]}
                    textField={"label"}
                    valueField={"value"}
                    placeholder={"Select Type"}
                    onChange={(val) => onChange(val.value)}
                    disabled
                  />
                )}
              />
            </div> */}
          </form>
          <div className="text-right">
            {renderLeadOption()}
            <button
              onClick={() => {
                if (props.onCancel) {
                  props.onCancel();
                }
              }}
              className="btn btn-outline-dark py-2 font-weight-semibold font-normal px-4 mr-1"
              disabled={updating}
            >
              Cancel
            </button>
            <button
              disabled={updating}
              type="submit"
              form="activity-form"
              className="btn btn-primary py-2 font-weight-semibold font-normal"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export interface ActivityModel {
  // date: Date;
  // endDate?: Date;
  // startTime: string;
  // endTime: string;
  timePeriod?: ITimePeriodData;
  location?: string;
  activityType?: string;
  type?: string;
  title?: string;
  modelCode?: string;
  variantCode?: string;
  year?: string;
  vehicleModel?: VehicleModelMinimal;
  vehicleVariant?: VehicleVariantMinimal;
  vehicle?: { _id: string; displayName: string };
  vehicleId?: string;
  salesPerson?: { _id: string; name: string; knownAs?: string };
  showroom?: { _id: string; title: string };
  leadId?: string;
  note?: string;
  eventId?: string;
  showRoom?: { _id: string; title: string };
  busyEvent?: boolean;
  address?: any;
  customer?: Customer;
  overnightTestdriveEnabled?: boolean;
  users?: Customer[];
}

export interface IconButtonModel {
  placeholder: string;
  type: string;
}

export const activityTypeToIconMapper: { [id: string]: IEventMapDetail } = {
  call: {
    icon: "icon-call",
    text: "Call",
    color: "EBFAEF",
    isDraggable: true,
    isSingleDayEvent: true,
    className: "call-event",
  },
  meeting: {
    icon: "icon-users-fill",
    text: "Meeting",
    color: "FDECE2",
    isDraggable: true,
    isSingleDayEvent: true,
    className: "meeting-event",
  },
  deadline: {
    icon: "icon-flag-fill",
    text: "Deadline",
    color: "FFF1F2",
    isDraggable: true,
    isSingleDayEvent: true,
    className: "deadline-event",
  },
  task: {
    icon: "icon-clock",
    text: "Task",
    color: "FDECE2",
    isDraggable: true,
    isSingleDayEvent: true,
    className: "task-event",
  },
  scheduleATestDrive: {
    icon: "icon-drive",
    text: "Schedule a test drive",
    color: "F2F2FF",
    isDraggable: true,
    isSingleDayEvent: true,
    className: "test-drive-event",
  },
  requestACallBack: {
    icon: "icon-call",
    text: "Request a callback",
    color: "EBFAEF",
    isDraggable: true,
    isSingleDayEvent: true,
    className: "request-callback-event",
  },
  showroomAppointment: {
    icon: "icon-building",
    text: "Showroom appointment",
    color: "F5EEFF",
    isDraggable: true,
    isSingleDayEvent: true,
    className: "showroom-appointment-event",
  },
  scheduleAShowroomVisit: {
    icon: "icon-building",
    text: "Showroom appointment",
    color: "F5EEFF",
    isDraggable: true,
    isSingleDayEvent: true,
    className: "showroom-appointment-event",
  },
  note: {
    icon: "icon-note",
    text: "Note",
    color: "F7F5CD",
    isDraggable: true,
    isSingleDayEvent: true,
    className: "note-event",
  },
  timeOff: {
    icon: "icon-clock",
    text: "Time Off",
    color: "F0F0F0",
    isDraggable: false,
    isSingleDayEvent: false,
    className: "time-off-event",
  },
  vehicleTimeOff: {
    icon: "icon-clock",
    text: "Vehicle Time Off",
    color: "F0F0F0",
    isDraggable: false,
    isSingleDayEvent: false,
    className: "time-off-event",
  },
};

export interface IEventMapDetail {
  icon: string;
  text: string;
  color: string;
  isDraggable: boolean;
  isSingleDayEvent: boolean;
  className: string;
}
